import React, { useState, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { ProtectedRoute } from '../auth/ProtectedRoute';
import { ClientesList } from '../clientes/ClientesList';
import { ClienteForm } from '../clientes/ClienteForm';
import { EntregaForm } from '../entregas/EntregaForm';
import { EntregasList } from '../entregas/EntregasList';
import { Menu, X } from 'lucide-react';
import { LoadingSpinner } from './LoadingSpinner';

// Lazy loading de páginas principales
const Dashboard = lazy(() => import('../../pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Clientes = lazy(() => import('../../pages/Clientes').then(m => ({ default: m.Clientes })));
const Entregas = lazy(() => import('../../pages/Entregas').then(m => ({ default: m.Entregas })));
const RutaHoy = lazy(() => import('../../pages/RutaHoy').then(m => ({ default: m.RutaHoy })));
const Inventario = lazy(() => import('../../pages/Inventario').then(m => ({ default: m.Inventario })));
const Comodato = lazy(() => import('../../pages/Comodato').then(m => ({ default: m.Comodato })));
const Ajustes = lazy(() => import('../../pages/Ajustes').then(m => ({ default: m.Ajustes })));
const Login = lazy(() => import('../../pages/Login').then(m => ({ default: m.Login })));

export const Layout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gradient-to-br dark:from-slate-950 dark:via-gray-900 dark:to-slate-900">
      {/* Header fijo en la parte superior */}
      <Header className="fixed top-0 left-0 right-0 z-30" />
      
      {/* Botón de menú móvil */}
      <button
        onClick={toggleSidebar}
        className="fixed top-4 left-4 z-40 lg:hidden bg-white dark:bg-gray-800 p-2 rounded-lg shadow-lg"
        aria-label="Toggle menu"
      >
        {isSidebarOpen ? (
          <X className="h-6 w-6 text-gray-600 dark:text-gray-300" />
        ) : (
          <Menu className="h-6 w-6 text-gray-600 dark:text-gray-300" />
        )}
      </button>

      <div className="flex pt-16"> {/* pt-16 para compensar el header fijo */}
        {/* Sidebar con overlay en móvil */}
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 z-20 transition-opacity lg:hidden ${
            isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onClick={toggleSidebar}
        />
        
        <Sidebar 
          className={`fixed lg:static h-[calc(100vh-4rem)] z-30 transition-transform duration-300 transform lg:transform-none ${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          onClose={() => setIsSidebarOpen(false)}
        />

        {/* Contenido principal */}
        <main className="flex-1 p-4 lg:p-6 w-full overflow-x-hidden">
          <div className="max-w-7xl mx-auto">
            <Suspense fallback={<LoadingSpinner size="lg" className="py-20" />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/" element={<ProtectedRoute><RutaHoy /></ProtectedRoute>} />
                <Route path="/resumen" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/dashboard" element={<Navigate to="/resumen" replace />} />
                <Route path="/clientes" element={<ProtectedRoute><Clientes /></ProtectedRoute>}>
                  <Route index element={<ClientesList />} />
                  <Route path="new" element={<ClienteForm />} />
                  <Route path="nuevo" element={<Navigate to="/clientes/new" replace />} />
                  <Route path=":id" element={<ClienteForm />} />
                </Route>
                <Route path="/entregas" element={<ProtectedRoute><EntregaForm /></ProtectedRoute>} />
                <Route path="/entregas/new" element={<ProtectedRoute><EntregaForm /></ProtectedRoute>} />
                <Route path="/entregas/nuevo" element={<Navigate to="/entregas/new" replace />} />
                <Route path="/entregas/list" element={<ProtectedRoute><Entregas /></ProtectedRoute>}>
                  <Route index element={<EntregasList />} />
                </Route>
                <Route path="/ruta-hoy" element={<Navigate to="/" replace />} />
                <Route path="/ruta" element={<Navigate to="/" replace />} />
                <Route path="/inventario" element={<ProtectedRoute><Inventario /></ProtectedRoute>} />
                <Route path="/inventario-equipos" element={<Navigate to="/comodato?tab=equipos" replace />} />
                <Route path="/comodato" element={<ProtectedRoute><Comodato /></ProtectedRoute>} />
                <Route path="/ajustes" element={<ProtectedRoute><Ajustes /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </div>
        </main>
      </div>

      {/* Eliminado el botón móvil ya que ahora está en el Sidebar */}
    </div>
  );
};